/**
 * The Component Registry: every component type the engine knows, and the one
 * place component data is parsed and validated against its schema.
 *
 * Parsing is tolerant, like Unreal loading an asset with stale properties:
 * unknown props are dropped and invalid values fall back to defaults, each
 * with a warning for the Output Log. Structural problems (unknown component
 * types, duplicate ids) are the serializer's job and fail the load.
 */
import { defaultForProp, validateProp } from "./propTypes";
import type { PropResult, PropType } from "./propTypes";
import type { ComponentData, ComponentSchema } from "./defineComponent";

export interface ParsedComponent {
  readonly data: ComponentData;
  readonly warnings: readonly string[];
}

export class ComponentRegistry {
  private readonly schemas = new Map<string, ComponentSchema>();

  register(schema: ComponentSchema): void {
    if (this.schemas.has(schema.type))
      throw new Error(`Component '${schema.type}' is already registered`);
    this.schemas.set(schema.type, schema);
  }

  has(type: string): boolean {
    return this.schemas.has(type);
  }

  get(type: string): ComponentSchema | undefined {
    return this.schemas.get(type);
  }

  /** All schemas in Inspector order. */
  all(): ComponentSchema[] {
    return [...this.schemas.values()].sort((a, b) => a.display.order - b.display.order);
  }

  /** Parses raw manifest data into complete, frozen component data. */
  parse(type: string, raw: unknown): ParsedComponent {
    const schema = this.require(type);
    const source = isRecord(raw) ? raw : {};
    const warnings: string[] = [];
    const data: Record<string, unknown> = {};

    for (const [prop, propType] of Object.entries(schema.props)) {
      const value = parseProp(propType, source[prop], (error) =>
        warnings.push(`${type}.${prop}: ${error}`),
      );
      if (value !== undefined) data[prop] = value;
    }
    const ignored = new Set(schema.ignoredKeys ?? []);
    for (const prop of Object.keys(source)) {
      if (!(prop in schema.props) && !ignored.has(prop))
        warnings.push(`${type}.${prop}: unknown property`);
    }

    return { data: Object.freeze(data), warnings };
  }

  /** Validates one prop value, e.g. for `entity.setProperty`. */
  validateProp(type: string, prop: string, value: unknown): PropResult {
    const propType = this.get(type)?.props[prop];
    if (!propType) return { ok: false, error: `${type} has no property '${prop}'` };
    return validateProp(propType, value);
  }

  private require(type: string): ComponentSchema {
    const schema = this.schemas.get(type);
    if (!schema) throw new Error(`Unknown component type '${type}'`);
    return schema;
  }
}

/** A present, valid value; else the default (warning when a present value was invalid). */
function parseProp(propType: PropType, raw: unknown, warn: (error: string) => void): unknown {
  if (raw === undefined) return defaultForProp(propType);

  const result = validateProp(propType, raw);
  if (result.ok) return result.value;

  warn(result.error);
  return defaultForProp(propType);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
